import { Panel, RoutedPanelProps } from '@/components/Layout/Panel/Panel';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useConfirm } from '@/components/ui/use-confirm';
import { formatBytes } from '@/lib/formatBytes';
import { CacheStats } from '@certhon/models';
import { RotateCcw, Trash2 } from 'lucide-react';
import { useMemo } from 'react';
import CacheEntriesTable from './cache.table';
import {
  useCacheEnabled,
  useCacheEntries,
  useCacheStats,
  useClearCache,
  useResetCacheStats,
  useSetCacheEnabled,
} from './cache.hooks';

/**
 * Beheer → Cache: what the API's HTTP cache holds and how it is doing.
 *
 * A hit is any request PostgREST did not have to answer: a 304, a stored
 * response, or one that joined an in-flight fetch. Bypassed requests are
 * uncacheable by shape and are shown but not counted against the ratio.
 */

interface Totals {
  entries: number;
  size: number;
  storedSize: number;
  hits: number;
  misses: number;
  bypassed: number;
  /** Body bytes a 304 spared the client. */
  notSent: number;
  /** Body bytes served without a PostgREST query. */
  servedFromStore: number;
  fetched: number;
}

function totalsOf(rows: CacheStats[]): Totals {
  const totals: Totals = {
    entries: 0,
    size: 0,
    storedSize: 0,
    hits: 0,
    misses: 0,
    bypassed: 0,
    notSent: 0,
    servedFromStore: 0,
    fetched: 0,
  };
  for (const row of rows) {
    totals.entries += row.entries;
    totals.size += row.size;
    totals.storedSize += row.stored_size;
    totals.hits +=
      row.revalidated_requests + row.served_requests + row.coalesced_requests;
    totals.misses += row.filled_requests;
    totals.bypassed += row.bypassed_requests;
    totals.notSent += row.revalidated_bytes;
    totals.servedFromStore += row.served_bytes + row.coalesced_bytes;
    totals.fetched += row.filled_bytes;
  }
  return totals;
}

const ratio = (hits: number, misses: number) =>
  hits + misses === 0 ? '–' : `${Math.round((hits / (hits + misses)) * 100)}%`;

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <Card>
      <CardHeader>
        <CardDescription>{label}</CardDescription>
        <CardTitle className="text-2xl tabular-nums">{value}</CardTitle>
      </CardHeader>
      {hint && (
        <CardContent className="text-muted-foreground text-xs">
          {hint}
        </CardContent>
      )}
    </Card>
  );
}

function ResourceRows({ rows }: { rows: CacheStats[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Resource</TableHead>
          <TableHead className="text-right">Entries</TableHead>
          <TableHead className="text-right">Size</TableHead>
          <TableHead className="text-right">Stored</TableHead>
          <TableHead className="text-right">Hits</TableHead>
          <TableHead className="text-right">Misses</TableHead>
          <TableHead className="text-right">Bypassed</TableHead>
          <TableHead className="text-right">Hit ratio</TableHead>
          <TableHead className="text-right">Not sent (304)</TableHead>
          <TableHead className="text-right">Served from store</TableHead>
          <TableHead className="text-right">Fetched</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          const hits =
            row.revalidated_requests +
            row.served_requests +
            row.coalesced_requests;
          return (
            <TableRow key={row.resource}>
              <TableCell className="font-medium">{row.resource}</TableCell>
              <TableCell className="text-right tabular-nums">
                {row.entries}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatBytes(row.size)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatBytes(row.stored_size)}
              </TableCell>
              <TableCell className="text-right tabular-nums">{hits}</TableCell>
              <TableCell className="text-right tabular-nums">
                {row.filled_requests}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {row.bypassed_requests}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {ratio(hits, row.filled_requests)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatBytes(row.revalidated_bytes)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatBytes(row.served_bytes + row.coalesced_bytes)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatBytes(row.filled_bytes)}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

export const CachePanel = ({
  params,
  path,
  basePath,
  ...panelProps
}: RoutedPanelProps) => {
  const stats = useCacheStats();
  const entries = useCacheEntries();
  const enabled = useCacheEnabled();
  const setEnabled = useSetCacheEnabled();
  const clear = useClearCache();
  const resetStats = useResetCacheStats();
  const { confirm, ConfirmDialog } = useConfirm();

  const totals = useMemo(() => totalsOf(stats.data ?? []), [stats.data]);

  const handleClear = async () => {
    const ok = await confirm({
      title: 'Clear the cache?',
      description:
        'Every stored response is dropped; the next request of each fills it again. Clients keep revalidating with their ETags.',
      confirmLabel: 'Clear',
      destructive: true,
    });
    if (ok) {
      clear.mutate(undefined);
    }
  };

  const handleResetStats = async () => {
    const ok = await confirm({
      title: 'Reset the statistics?',
      description: 'All request counters go back to zero.',
      confirmLabel: 'Reset',
    });
    if (ok) {
      resetStats.mutate(undefined);
    }
  };

  return (
    <Panel {...panelProps}>
      <div className="space-y-4 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              id="cache-enabled"
              checked={enabled.data ?? true}
              disabled={enabled.isLoading || setEnabled.isPending}
              onCheckedChange={(checked) => setEnabled.mutate(checked)}
            />
            <Label htmlFor="cache-enabled">
              {enabled.data === false ? 'Cache disabled' : 'Cache enabled'}
            </Label>
          </div>
          <div className="ml-auto flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetStats}
              disabled={resetStats.isPending}
            >
              <RotateCcw /> Reset statistics
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleClear}
              disabled={clear.isPending}
            >
              <Trash2 /> Clear cache
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Stat
            label="Entries"
            value={String(totals.entries)}
            hint={`${formatBytes(totals.storedSize)} at rest, ${formatBytes(totals.size)} uncompressed`}
          />
          <Stat
            label="Hit ratio"
            value={ratio(totals.hits, totals.misses)}
            hint={`${totals.hits} hits, ${totals.misses} misses, ${totals.bypassed} bypassed`}
          />
          <Stat
            label="Not sent"
            value={formatBytes(totals.notSent)}
            hint="Bodies clients already had (304)"
          />
          <Stat
            label="Served from store"
            value={formatBytes(totals.servedFromStore)}
            hint={`Without a PostgREST query; ${formatBytes(totals.fetched)} fetched upstream`}
          />
        </div>

        <ResourceRows rows={stats.data ?? []} />
      </div>

      <CacheEntriesTable data={entries.data} isLoading={entries.isLoading} />
      <ConfirmDialog />
    </Panel>
  );
};
